import styled from "styled-components/macro";
import { Modal as AntModal } from "antd";
import { typography } from "src/utils/styledComponents/typography";
import { IPAD } from "src/utils/constants/media";
import { colors } from "src/theme/colors";

export const Container = styled.div``;

export const Modal = styled(AntModal)`
  /**
   * A confirmation is a short message, not a page.
   *
   * This was width={"60%"} in the component: 864px on a 1440 screen, where the
   * content is an icon, a line of text and a button, so it read as a mostly
   * empty sheet with the words lost in the middle of it. A fixed, readable
   * width sizes it to what it says, and the rule below already takes it to
   * full width on a phone.
   */
  &.ant-modal {
    width: 480px;
    max-width: calc(100vw - 32px);

    /* The foot was pulled fifty pixels UP, over the body — measured, not
       guessed: the body's bottom edge sat 50px below the foot's top edge, so
       the last line of every confirmation ran underneath the action band and
       was sliced in half. "Your withdrawal request has been successfully
       processed." lost the word "processed."

       Nothing is needed in its place: the dialog chrome already lays the
       foot out as a sunk band, right-aligned with its own padding and
       hairline. See ui/Modal. */
    ${typography.CustomText("1em")}
    .ant-modal-body {
      padding-top: 0px;
    }
    .ant-modal-header {
      border-bottom: 0px;
      .ant-modal-title {
        text-align: center;
      }
    }
  }

  @media (max-width: ${IPAD}px) {
    flex-wrap: wrap;
    width: 100% !important;

    &.ant-modal {
      width: 100%;
    }
  }
`;

export const Header = styled.div`
  margin-top: 1em;
  font-weight: 700;
  color: ${colors.primary[500]};
  ${typography.CustomMeriText("1.5em")}
`;

export const Small = styled.span`
  font-size: 1em;
  opacity: 0.5;
  text-align: center;
  width: 38%;
`;

export const DoneContainer = styled.div`
  display: flex;
  flex-direction: column;
  ${typography.CustomText("1em")}
  align-items: center;
  justify-content: center;
  /* One rhythm for the whole column, instead of margins on each piece. */
  gap: 12px;
  padding: 4px 0 8px;

  /* The illustration was drawn at its natural size — around 200px of party
     popper above two short lines. It is a mark on a confirmation, not the
     subject of it. */
  > img {
    width: 72px;
    height: auto;
  }

  ${Small} {
    width: 45%;
  }
`;

/**
 * The line that says what happened.
 *
 * It was set in a 1.5em blue serif at 700, with a 23px right margin that
 * pushed it off centre — a face used nowhere else in the product, louder than
 * the dialog's own title above it, which is the exact treatment DESIGN.md
 * calls out for dialog headings. A success message is a sentence, not a
 * banner.
 *
 * `color` is still honoured, since callers pass a tone for their own state.
 */
export const DoneText = styled.span`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.014em;
  text-align: center;
  /* Ink, like every other dialog heading in the product.
     Both confirmations set this to success[500] — #12B76A, a mint that is not
     the product's success colour (#039855 is, and it is success[600]) — so
     the one line the dialog exists to say was in a green that appears nowhere
     else, at heading size. A confirmation does not need to be green to read
     as good news; the mark above it already says so. The color prop is
     still honoured for a caller that genuinely needs a tone. */
  color: ${({ color }) => color || colors.gray[900]};
`;

/**
 * The gaps between the lines of a confirmation.
 *
 * `margin-top: 2.5rem` on every one of these put FORTY pixels between the
 * icon and the line under it, and forty more between that and the sentence
 * under that — in a dialog whose whole content is an illustration and two
 * short lines. It read as three unrelated things stacked in a tall white box
 * rather than one message. (There were two `font-size` declarations here as
 * well, the first of them dead.)
 *
 * The container below sets the rhythm now, in one place.
 */
export const DoneFrame = styled.div<{ width?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ width }) => (width ? width : "auto")};
  font-size: 1rem;

  .ant-typography {
    text-align: center;
  }
`;
